/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/AttendanceModern.tsx
 * PURPOSE: Modern attendance management với GPS và photo verification.
 *          2026-08-11: fixed BS-301 Route 2 AC #10 (SC-017-EMP-ATTENDANCE bug G1, P0) — page
 *          title leaked the React component name "AttendanceModern" instead of
 *          the Vietnamese business label.
 *          2026-08-12: sync mobile PR #603 (BS-300 §1bis) — non-blocking
 *          late/early clock-in warning banners (G2/G5) + watermarked photo.
 *          2026-08-15: web-sync checklist A.2 — added the shared
 *          EmployeeBottomNav (this route previously had no bottom nav at all).
 *          2026-08-15: web-sync checklist A.5 — branch/location fetch error
 *          banner + "Thử lại" retry button (useAttendanceModernClock's
 *          branchConfigError / retryBranchConfig).
 *          2026-08-17: BS-301 Route 2 §1 gaps — (1) "Hôm nay không có ca"
 *          empty-state (hides the clock-in card + button entirely instead of
 *          always showing it regardless of whether the employee has a shift
 *          today); (2) GPS-lệch (out-of-geofence) banner after a successful
 *          clock-in, non-blocking per BS-300 §1bis-G1.
 *          2026-09-04: SC-017-EMP-ATTENDANCE [1] home — closed the 3 gaps
 *          between this page's `home` screenState and the wireframe's 3
 *          mutually-exclusive branches (registry ANNOTATION [1]):
 *          (1) DEFAULT was a generic "Chưa có dữ liệu chấm công" placeholder
 *          card — now renders the real "Lịch ca tuần này" week-grid card
 *          (tap-through to /schedule) + "Việc khẩn cấp (N)"/"Việc phát sinh
 *          (N)" task cards (both via new useAttendanceHomeExtras.ts) + a GPS
 *          status line (branch name/accuracy/distance, from data
 *          useAttendanceModernClock already computes but this page never
 *          rendered) above the existing clock-in button;
 *          (2) IN-SHIFT had 3 buttons (including a "Kiểm tra vị trí" one not
 *          in the wireframe) and no elapsed-time text — now exactly the 2
 *          wireframe buttons ("Xem checklist ca (tuỳ chọn)"/"Chấm công ra")
 *          plus "{N} phút/giờ đã trôi qua" (new `elapsedMinutes` from
 *          useAttendanceModernClock, derived from the `last_clock_in` field
 *          GET /attendance/status already returned but this hook never read);
 *          (3) STALE-OPEN was entirely unimplemented — added, detecting "≥1
 *          attendance record with clock_out IS NULL from before today" via
 *          the real self-history endpoint (see useAttendanceHomeExtras.ts
 *          header for why NOT the pre-checkin-view `is_stale` field mobile's
 *          TS types declare — that field doesn't actually exist on the
 *          backend response). STALE-OPEN takes precedence over both other
 *          branches per the wireframe ("chặn toàn bộ 2 nhánh kia"). KNOWN
 *          BACKEND GAP (not introduced here, not in this PR's scope): the
 *          confirm sheet's "Xác nhận" reuses the same real clockOut() mobile
 *          also reuses for this — but both v1 `/attendance/clock-out` and v2
 *          `/attendance/check-out` only search for an open record with
 *          `clock_in >= today_start`, so closing a genuinely-stale
 *          (prior-day) record will 400 on this backend today; the error
 *          surfaces honestly via the hook's existing `error` state rather
 *          than being hidden. Flagged for backend follow-up.
 *          2026-09-04 (mobile-source-of-truth audit, reading
 *          mobile/src/screens/attendance/AttendanceScreen.tsx directly instead
 *          of the wireframe doc's ASCII summary of it): mobile's AppHeader
 *          renders a per-state back button (headerConfig object,
 *          AttendanceScreen.tsx:1279-1318); this page's header never had a
 *          per-state equivalent, which left 2 of the 11 states as real dead
 *          ends — capture_photo (no way back to method_select once GPS method
 *          was chosen; CameraCapture hides its internal Cancel when
 *          required=true, same as mobile's PhotoCapture, but mobile still has
 *          the header back) and end_verify (no way back to checklist at all).
 *          Added an explicit ‹ back button to both (capture_photo here,
 *          end_verify via EndVerifyScreen's new `onBack` prop) rather than
 *          building a full contextual header — every other state already had
 *          an equivalent affordance (Huỷ/Đổi phương thức/‹ close) built in an
 *          earlier pass. See EMPLOYEE-CODER-NOTES.md-style audit notes in this
 *          session for the other findings (tenant-key bug fixed in
 *          useAttendanceModernClock.ts; several apparent gaps confirmed to be
 *          non-issues or mobile itself lagging a newer ratified BR — not
 *          web bugs — see conversation report, not restated here to avoid
 *          drift between this header and the actual finding).
 *          2026-09-04 (structural layout parity — read-only structural audit
 *          this session comparing this file + screens/ against mobile's
 *          AttendanceScreen.tsx line-by-line): 3 root causes fixed. (1) mobile
 *          only Card-wraps semantic status cards (home/DEFAULT week-card,
 *          home/IN-SHIFT); every other screen (method_select/capture_photo/
 *          gps_clock_in_form/clock_in_success/checklist/end_verify/
 *          end_verify_success) is a bare fragment spaced by `.main`'s new
 *          `gap` — those 7 screens' `.card` wrappers were removed (see each
 *          screens/*.tsx file's own changelog). (2) mobile's AppHeader is
 *          state-driven (a headerConfig record, AttendanceScreen.tsx:
 *          1279-1318) — this page's header was static, forcing 4 screens to
 *          build a duplicate in-body title+back header. Replaced the static
 *          header with the `headerConfig` map below (mirrors mobile's shape)
 *          and removed the now-redundant duplicate headers from
 *          capture_photo (here), ChecklistScreen.tsx, EndVerifyScreen.tsx,
 *          history (here), MethodSelectScreen.tsx, and the duplicate title +
 *          redundant "‹ Đổi phương thức" button from ClockInConfirmScreen.tsx
 *          (its back-to-method_select is now solely the header back button,
 *          matching mobile — no functionality lost). (3) added `gap` to
 *          `.main` (replaces the one-off `.weekScheduleCard` margin hack) for
 *          even spacing between the now-flat siblings. Per-screen: moved
 *          `branchConfigError` to be scoped inside home/DEFAULT (was
 *          page-global); de-carded home/DEFAULT's trailing "GPS status +
 *          clock-in button" block into 2 bare siblings; de-carded
 *          home/STALE-OPEN and moved its "liên hệ quản lý" note back out to a
 *          sibling position; added the missing deadline meta line to
 *          urgent/adhoc task rows (kept the existing "Xem" button — both now
 *          coexist, matching mobile's ListItem `meta` + this session's
 *          earlier verified "Xem" addition). Business logic/data-fetching
 *          untouched — see this session's conversation report for the full
 *          audit findings this entry implements.
 *          2026-09-04 (pixel-mockup visual/style audit — design source
 *          docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/
 *          employee-app-v2/attendance.html, NOT the ASCII wireframe doc):
 *          closed 3 confirmed VISUAL gaps vs the mockup (state-machine logic
 *          untouched, already verified this session):
 *          (1) top-level header rebuilt from a 2-row "centered title +
 *          'Về Dashboard' pill" into the mockup's single-row app-header
 *          (‹ back → /dashboard, title, real notification bell reusing the
 *          same useNotifications()/NotificationDropdown wiring
 *          EmployeeDashboard.tsx already ships — dot shown only when the
 *          real unreadCount > 0, no fabricated count);
 *          (2) page/header/main widened-to-1400px desktop layout replaced
 *          with the same max-width:480px centered app-shell pattern already
 *          proven on EmployeeDashboard.module.css:34-40;
 *          (3) task rows in "Việc khẩn cấp"/"Việc phát sinh" gained the
 *          mockup's outlined "Xem" button — confirmed REAL (not decorative)
 *          per WIREFRAME-EMPLOYEE-FLOW.md:2840 ("[Xem] trên việc khẩn/phát
 *          sinh ──► Nhiệm vụ của tôi") and the mockup's own href target
 *          (utilities.html#emp-43 "Nhiệm vụ của tôi") — both point at the
 *          same real `/tasks` route (TasksProfessional.tsx, already linked
 *          from Utilities.tsx as "Nhiệm vụ của tôi"). No per-task deep link
 *          exists yet (TasksProfessional has no taskId param), so "Xem"
 *          navigates to the list, same as the static mockup itself does
 *          (every "Xem" anchor in the mockup points at the same single
 *          target regardless of which task). Reconciles the earlier
 *          "mobile ListItem has no such action, not a bug" finding
 *          (commit c0b9e484b): that was evaluated against mobile's current
 *          CODE, not this design source — WIREFRAME-EMPLOYEE-FLOW.md:2840
 *          shows the button IS intended design-wide, so mobile is a
 *          separate, pre-existing drift from its own design source, not a
 *          reason to omit it here. Mobile itself is out of this PR's scope
 *          (different codebase/repo path, not touched).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN §Route 2
 *   + BS-300-PHAN-DOAN-C-NHAN-VIEN-CHAM-CONG §1bis (mobile↔web parity)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: GPS-validated attendance with Vietnamese Labor Law compliance
 *
 * @business-context
 * - Domain: Page Routing, Vietnamese F&B Application Navigation
 * - Impact: User Experience, Application Flow, Navigation
 * - Stakeholders: End Users, Product Team, Development Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Page Layout, Component Composition
 * - Dependencies: React 18, React Router, TypeScript, Layout Components
 *
 * @cross-references
 * - Design: docs/01-planning/02-uat-len-lich-cham-cong/BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN.md
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 * - Tests: frontend/tests/pages/AttendanceModern.test.tsx
 *  *
 * 2026-09-04 — LỆCH CHỦ ĐÍCH so với mobile (chỉ đạo product owner): xong chấm
 * công VÀO ("Xong") và xong chấm công RA ("Về trang chủ") đều điều hướng
 * THẲNG về /dashboard/staff. Mobile giữ người dùng lại màn chấm công
 * (goHome() → screenState 'home', AttendanceScreen.tsx), nghĩa là phải bấm
 * thêm một lần nữa mới rời được — trên web có bottom-nav luôn hiện nên việc
 * giữ lại đó không mang thêm thông tin gì, chỉ tốn một thao tác.
 *
 * 2026-09-07 (BOT-025 #752, chỉ đạo trực tiếp trong phiên) — ĐẢO NGƯỢC phần
 * "Hôm nay không có ca" của mục 2026-08-17 phía trên: block chặn nút chấm
 * công (`showNoShiftToday`) đã bị GỠ. Lý do: dòng Gherkin gốc trong
 * `BS-301-R2 §1` ("KHÔNG hiển thị nút chấm công" khi không có ca) mâu thuẫn
 * với `BS-300`/mobile (0 gate tương đương)/wireframe `SC-019-EMP-HOME` —
 * "chấm công ngoài lịch" là năng lực đã có sẵn ở backend (schedule_id
 * optional tại clock-in). Chốt: màn Trang chủ (route khác) không có CTA chấm
 * công khi không có ca — giữ nguyên; nhưng NGAY TẠI tab Chấm công (route
 * này), nhân viên luôn chấm công được, không ràng buộc theo `hasShiftToday`.
 * `BS-301-R2 §1` đã cập nhật lại Gherkin cho khớp (spec-sync, Debugging
 * Pattern #11).
 *
 * 2026-09-15 (bot-060, GH#871) — popup Timemark "Đã gửi — chờ Quản lý duyệt"
 * nhận `onSuccess={closeTimemark}`, mà `closeTimemark` chỉ `setShowTimemark(false)`
 * (useAttendanceModernClock.ts) → bấm "Về trang chủ" chỉ đóng modal, người dùng
 * vẫn ở `/attendance`: nhãn nút KHÔNG khớp hành vi. Sửa: `onSuccess` đóng modal
 * rồi `navigate('/dashboard/staff')` — đúng mục 2026-09-04 ngay trên ("Về trang
 * chủ" trên web đi THẲNG /dashboard/staff). `onCancel` ("Hủy"/X, hủy TRƯỚC khi
 * gửi) giữ nguyên chỉ-đóng-modal nên 2 callback không còn dùng chung nữa.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAttendanceModernClock } from '@/features/attendanceModern/useAttendanceModernClock';
import { useAttendanceHomeExtras } from '@/features/attendanceModern/useAttendanceHomeExtras';
import { useAuthStore } from '@/store/auth';
import apiWrapper from '@/services/api/apiWrapper';
import { locationService } from '@/services/location';
// 2026-09-04 (visual/style gap #2, header) — reuses the SAME real
// notification wiring EmployeeDashboard.tsx already ships (real
// unreadCount from the backend, no fabricated data) rather than inventing
// a second bell implementation.
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationDropdown } from '@/components/navigation/NotificationDropdown';
import { EmployeeBottomNav } from '@/components/navigation/EmployeeBottomNav';
import { TimemarkForm } from '@/components/TimemarkForm';
import { MethodSelectScreen } from '@/features/attendanceModern/screens/MethodSelectScreen';
import { CameraCapture } from '@/features/attendanceModern/screens/CameraCapture';
import { ClockInConfirmScreen } from '@/features/attendanceModern/screens/ClockInConfirmScreen';
import { ClockInSuccessScreen } from '@/features/attendanceModern/screens/ClockInSuccessScreen';
import { ChecklistScreen } from '@/features/attendanceModern/screens/ChecklistScreen';
import { EndVerifyScreen } from '@/features/attendanceModern/screens/EndVerifyScreen';
import { EndVerifySuccessScreen } from '@/features/attendanceModern/screens/EndVerifySuccessScreen';
import { HistoryContent } from '@/features/attendanceModern/screens/HistoryContent';
import styles from './AttendanceModern.module.css';

// SC-017-EMP-ATTENDANCE (2026-09-02) — mirrors mobile's ScreenState union
// (AttendanceScreen.tsx:132-143). Web only implements the states that exist
// today; states not yet built (capture_photo/clock_in_confirm/clock_in_success/
// checklist/end_verify/end_verify_success/history) are added in later PRs per
// the approved build order — 'home' still owns the GPS clock-in form inline
// until [3]/[4]/[5] replace it.
type ScreenState = 'home' | 'method_select' | 'capture_photo' | 'gps_clock_in_form' | 'clock_in_success' | 'checklist' | 'end_verify' | 'end_verify_success' | 'history';

// SC-017-EMP-ATTENDANCE [1] home (2026-09-04) — "{N} phút/giờ đã trôi qua" /
// "Đã mở: {thời lượng}" formatting, shared by IN-SHIFT and STALE-OPEN.
function formatDurationVi(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes} phút`;
  if (minutes === 0) return `${hours} giờ`;
  return `${hours} giờ ${minutes} phút`;
}

// STALE-OPEN "Ca {ngày}" — "Hôm qua" when the open shift's clock-in date is
// literally yesterday (the common case), otherwise the literal date.
function formatStaleDateVi(iso: string): string {
  const d = new Date(iso);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Hôm qua';
  return d.toLocaleDateString('vi-VN');
}

const WEEKDAY_LABELS_VI = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

export const AttendanceModern: React.FC = () => {
  const navigate = useNavigate();
  // Production ready attendance tracking
  const { status, serverStatus, staleCloseMode, currentAttendanceId, elapsedMinutes, error, message, accuracyInfo, photoBase64, timingWarning, clockOutTimingWarning, geofenceResult, branchConfig, earlyClockinToleranceMinutes, branchConfigError, methodConfig, gpsDeviationWarning, showTimemark, capturePhoto, validateLocation, clockIn, clockOut, overrideAccuracy, retryBranchConfig, triggerTimemark, closeTimemark } = useAttendanceModernClock();
  // SC-017-EMP-ATTENDANCE [1] home (2026-09-04) — week schedule + task cards
  // (DEFAULT) + stale-open detection (STALE-OPEN). See useAttendanceHomeExtras.ts
  // header for why STALE-OPEN detection doesn't go through pre-checkin-view.
  const { weekSchedule, urgentTasks, adhocTasks, staleOpen, staleOpenChecked, staleOpenMinutes, recheckStaleOpen } = useAttendanceHomeExtras();
  const { user } = useAuthStore();
  // 2026-09-04 (visual/style gap #2, header) — real notification bell, same
  // hook/dropdown pair EmployeeDashboard.tsx already wires up. Mockup shows
  // only a plain dot (not a numeric badge like EmployeeDashboard's), so the
  // dot's visibility is driven by the real unreadCount > 0 rather than
  // rendering a count — still real data, just a different visual treatment.
  const { unreadCount } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [screenState, setScreenState] = useState<ScreenState>('home');
  const [clockInSuccessTime, setClockInSuccessTime] = useState<string>('');
  const [clockOutSuccessTime, setClockOutSuccessTime] = useState<string>('');
  // SC-017-EMP-ATTENDANCE [1] home STALE-OPEN (2026-09-04) — "[SHEET] xác
  // nhận đóng ca tồn đọng", opened from the STALE-OPEN card's CTA.
  const [showStaleConfirm, setShowStaleConfirm] = useState(false);
  // #780 (DD-0110 §2.2): close without GPS, tenant mode employee_then_review.
  const [staleReason, setStaleReason] = useState('');
  const [staleCloseError, setStaleCloseError] = useState<string | null>(null);
  const [staleClosing, setStaleClosing] = useState(false);
  // bot-060 follow-up (GH#871, 2026-09-16) — Employee path trước đây KHÔNG
  // có toast nào cho Timemark (onSuccess chỉ đóng modal + navigate). Thêm
  // toast state cùng pattern với ManagerProfilePage.tsx (auto-ẩn sau 4s).
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);
  const canOverrideAccuracy = locationService.isAdminOverrideAllowed(user?.role);
  const loading = status === 'locating' || status === 'submitting';
  // True when user is currently clocked in (server confirms)
  const isClockedIn = serverStatus === 'clocked_in';
  // SC-017-EMP-ATTENDANCE [1] home STALE-OPEN (2026-09-04) — "còn ≥1 bản ghi
  // clock_out IS NULL từ TRƯỚC hôm nay ⇒ chặn toàn bộ 2 nhánh kia" (registry
  // ANNOTATION [1]). Highest-precedence branch — checked before both
  // "no shift today" and IN-SHIFT below.
  const isStaleOpen = staleOpenChecked && !!staleOpen;
  // GỠ 2026-09-07 (BOT-025 #752, chỉ đạo trực tiếp trong phiên làm việc):
  // BS-301 Route 2 §1's "Empty-state — Hôm nay không có ca" Gherkin (chặn
  // hẳn nút chấm công trên chính tab Chấm công) mâu thuẫn với BS-300/mobile/
  // wireframe SC-019-EMP-HOME — "chấm công ngoài lịch" là năng lực đã có sẵn
  // ở backend (schedule_id optional tại clock-in), mobile không hề có gate
  // này. Chốt lại: màn Trang chủ (route khác, EmployeeDashboard.tsx) không
  // hiển thị CTA chấm công khi không có ca — đúng như trước; nhưng NGAY TẠI
  // tab Chấm công (route này), nhân viên luôn chấm công được, không ràng
  // buộc theo `hasShiftToday`. `showNoShiftToday` không còn dùng để chặn UI
  // nữa — biến `hasShiftToday`/`todayScheduleChecked` vẫn giữ trong hook để
  // dùng cho mục đích hiển thị khác (nếu có) sau này, không xoá khỏi hook.

  // SC-017-EMP-ATTENDANCE [1] home DEFAULT (2026-09-04) — "📍 {branch} — Đã
  // xác định · sai số ±{N}m" / "📏 Cách {N}m" per the wireframe. GPS wasn't
  // fetched proactively before (only once the employee reached [2]→[3]), so
  // this card had nothing real to show. Mirrors the existing loading card's
  // own copy ("đang kiểm tra GPS và chuẩn bị giao diện chấm công") — that
  // promise was never actually kept until now. Gated on serverStatus having
  // resolved (avoid firing during the initial unresolved window) and not
  // already clocked in / not stale-open (neither of those branches needs it).
  useEffect(() => {
    if (serverStatus && serverStatus !== 'clocked_in' && !isStaleOpen) {
      validateLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverStatus, isStaleOpen]);

  // SC-017-EMP-ATTENDANCE structural layout parity (2026-09-04, root cause
  // #2) — mirrors mobile's `headerConfig` (AttendanceScreen.tsx:1279-1318): a
  // per-screenState {title, onBack} map so this page's header supplies the
  // right title + back-target for every screen instead of being static
  // ("Chấm công" / → /dashboard, unconditionally), which is what forced
  // several screens to build their own duplicate in-body title+back header
  // (now removed from each — see this file's header changelog).
  const headerConfig: Record<ScreenState, { title: string; onBack: () => void }> = {
    home: { title: 'Chấm công', onBack: () => navigate('/dashboard/staff') },
    method_select: { title: 'Chọn phương thức', onBack: () => setScreenState('home') },
    capture_photo: { title: 'Chụp ảnh xác nhận', onBack: () => setScreenState('method_select') },
    gps_clock_in_form: { title: 'Chấm công vào — GPS', onBack: () => setScreenState('method_select') },
    clock_in_success: { title: 'Chấm công vào — GPS', onBack: () => navigate('/dashboard/staff') },
    checklist: { title: 'Checklist ca', onBack: () => setScreenState('home') },
    end_verify: {
      title: 'Xác thực chấm công ra',
      // Mirrors mobile's headerConfig.end_verify.onBack → 'checklist'. Falls
      // back to 'home' when there's no attendance id to show a checklist for
      // (ChecklistScreen requires one) — same fallback the old in-screen
      // back button used.
      onBack: () => setScreenState(currentAttendanceId ? 'checklist' : 'home'),
    },
    end_verify_success: { title: 'Xác thực chấm công ra', onBack: () => navigate('/dashboard/staff') },
    history: { title: 'Lịch sử chấm công', onBack: () => setScreenState('home') },
  };
  const currentHeader = headerConfig[screenState];

  return (
    <div className={styles.page}>
      {/* 2026-09-04 (visual/style gap #1) — single-row app-header: ‹ back +
          title + notification bell, matching attendance.html's .app-header
          (was a 2-row centered title + separate "Về Dashboard" pill).
          2026-09-04 (structural parity, root cause #2) — title/back now
          state-driven via `headerConfig` above, mirrors mobile's AppHeader. */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <button
            type="button"
            onClick={currentHeader.onBack}
            className={styles.headerBackButton}
            aria-label="Quay lại"
            data-testid="attendance-header-back"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <div className={styles.headerTitleWrap}>
            <h1 id="attendance-modern-title" className={styles.title}>{currentHeader.title}</h1>
          </div>
          <div ref={notifRef} className={styles.notifWrap}>
            <button
              type="button"
              onClick={() => setShowNotifications((v) => !v)}
              aria-label={`Thông báo${unreadCount > 0 ? ` (${unreadCount} chưa đọc)` : ''}`}
              className={styles.notifButton}
              data-testid="attendance-header-bell"
            >
              🔔
              {unreadCount > 0 && <span className={styles.notifDot} aria-hidden="true" />}
            </button>
            {showNotifications && (
              <div className={styles.notifBackdrop} onClick={() => setShowNotifications(false)} />
            )}
            {showNotifications && (
              <div className={styles.notifDropdown}>
                <NotificationDropdown onClose={() => setShowNotifications(false)} />
              </div>
            )}
          </div>
        </div>
      </header>

      <main aria-labelledby="attendance-modern-title" className={styles.main}>
        {/* Loading state */}
        {loading && (
          <div role="status" aria-live="polite" className={styles.card}>
            <h2 className={styles.cardTitle}>Đang xác thực vị trí...</h2>
            <p className={styles.cardDescription}>
              Vui lòng chờ trong giây lát. Hệ thống đang kiểm tra GPS và chuẩn bị giao diện chấm công.
            </p>
          </div>
        )}

        {/* SC-017-EMP-ATTENDANCE [1] home STALE-OPEN (2026-09-04) — "còn ca
            hôm qua chưa chấm công ra", chặn toàn bộ 2 nhánh kia (DEFAULT/
            IN-SHIFT). Registry ANNOTATION [1] + wireframe lines 6173-6197.
            2026-09-04 (structural layout parity, root cause #1): mobile's
            equivalent (isStaleOpen branch of renderHome) is NOT Card-wrapped
            either — it's a bare fragment of an EmptyState-equivalent block +
            a separate sibling contact-note Text (AttendanceScreen.tsx:
            608-623). Removed the `.card` box here and moved the "Liên hệ
            quản lý..." note back out to a true sibling (it was nested inside
            the card boundary before). */}
        {!loading && isStaleOpen && screenState === 'home' && staleOpen && (
          <>
            <div data-testid="stale-open-card">
              <p className={styles.staleOpenIcon} aria-hidden="true">⚠</p>
              <p className={styles.staleOpenTitle}>Bạn còn ca hôm qua chưa chấm công ra.</p>
              <p className={styles.cardDescription}>
                Ca {formatStaleDateVi(staleOpen.clockInAt)} · vào lúc{' '}
                {new Date(staleOpen.clockInAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} · Đã
                mở: {formatDurationVi(staleOpenMinutes ?? 0)}
              </p>
              <div className={styles.actionsRow}>
                <button
                  type="button"
                  onClick={() => setShowStaleConfirm(true)}
                  className={styles.primaryButton}
                  data-testid="stale-open-confirm-btn"
                >
                  Chấm công ra ngay bây giờ
                </button>
              </div>
            </div>
            <p className={styles.cardDescription}>Liên hệ quản lý nếu bạn cần điều chỉnh giờ chấm công.</p>
          </>
        )}

        {/* SC-017-EMP-ATTENDANCE [1] home IN-SHIFT (2026-09-04) — wireframe
            lines 6158-6170: title + "( Đang làm )" + elapsed-time text +
            EXACTLY 2 buttons. Was 3 buttons (including "Kiểm tra vị trí",
            not in the wireframe — validateLocation still runs internally at
            the start of clockOut(), so dropping the manual-recheck button
            loses no real functionality) + raw accuracy/error text blocks not
            in the wireframe either (real errors from an actual clock-out
            attempt surface on the [9] end_verify screen's own errorMessage
            prop instead). */}
        {!loading && !isStaleOpen && isClockedIn && screenState === 'home' && (
          <div className={styles.card} data-testid="in-shift-card">
            <h2 className={styles.cardTitle}>Chấm công</h2>
            <p className={styles.inShiftStatus}>( Đang làm )</p>
            {elapsedMinutes !== null && (
              <p className={styles.cardDescription} data-testid="elapsed-time-text">
                {formatDurationVi(elapsedMinutes)} đã trôi qua
              </p>
            )}
            <div className={styles.actionsRow}>
              {currentAttendanceId && (
                <button
                  onClick={() => setScreenState('checklist')}
                  className={styles.secondaryButton}
                  data-testid="view-checklist-btn"
                >
                  Xem checklist ca (tuỳ chọn)
                </button>
              )}
              <button onClick={() => setScreenState('end_verify')} className={styles.primaryButton} disabled={loading}
                data-testid="clock-out-btn">
                Chấm công ra
              </button>
            </div>
          </div>
        )}

        {!loading && isClockedIn && screenState === 'end_verify' && (
          <EndVerifyScreen
            geofenceResult={geofenceResult}
            isLate={clockOutTimingWarning.isLate}
            lateMinutes={clockOutTimingWarning.lateMinutes}
            loading={loading}
            errorMessage={error || message}
            // 2026-09-04 (structural parity, root cause #2): back-to-checklist
            // is now solely the page header's back button (headerConfig.end_verify
            // above) — EndVerifyScreen no longer owns a duplicate in-body back.
            onConfirm={async (notes) => {
              const ok = await clockOut(notes);
              if (ok) {
                setClockOutSuccessTime(new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }));
                setScreenState('end_verify_success');
              }
            }}
          />
        )}

        {!loading && screenState === 'end_verify_success' && (
          <EndVerifySuccessScreen
            time={clockOutSuccessTime}
            branchName={branchConfig?.branch_name ?? null}
            isLate={clockOutTimingWarning.isLate}
            lateMinutes={clockOutTimingWarning.lateMinutes}
            onViewHistory={() => setScreenState('history')}
            // 2026-09-04 (chỉ đạo product owner): xong chấm công RA thì về
            // THẲNG Trang chủ, không quay lại màn chấm công. Xem ghi chú
            // lệch-mobile ở đầu file.
            onGoHome={() => navigate('/dashboard/staff')}
          />
        )}

        {/* 2026-09-04 (structural parity, root cause #2): back-to-home is now
            solely the page header's back button (headerConfig.checklist
            above) — ChecklistScreen no longer owns a duplicate in-body
            title+close header (`onClose` prop removed). */}
        {!loading && isClockedIn && screenState === 'checklist' && currentAttendanceId && (
          <ChecklistScreen
            attendanceId={currentAttendanceId}
            onClockOut={() => setScreenState('end_verify')}
          />
        )}

        {/* 2026-09-04 (structural parity, root cause #2): removed the
            duplicate in-body title+back header — the page header
            (headerConfig.history above) now supplies "Lịch sử chấm công" +
            back-to-home. This screenState is NOT one of the 7 de-carded
            screens (root cause #1) — its `.card` wrapper stays. */}
        {!loading && screenState === 'history' && (
          <div className={styles.card}>
            <HistoryContent onClockInCta={() => setScreenState('home')} />
          </div>
        )}

        {/* SC-017-EMP-ATTENDANCE [1] home DEFAULT (2026-09-04) — wireframe
            lines 6131-6155: "Lịch ca tuần này" week-grid card → "Việc khẩn
            cấp (N)"/"Việc phát sinh (N)" task cards (both informational only
            — registry: "KHÔNG chặn chấm công") → branchConfigError banner
            (2026-09-04 structural parity: scoped HERE, matching mobile's
            Banner placement inside the DEFAULT branch just before
            LocationStatus — AttendanceScreen.tsx:746-758 — was page-global
            before) → GPS/branch status line → the clock-in button (CTA đi
            qua [2] method_select trước, KHÔNG mở form chấm công trực tiếp).
            Status line + button are 2 bare siblings (2026-09-04 structural
            parity: de-carded — mobile keeps LocationStatus + the CTA button
            as bare siblings too, not merged into one card). */}
        {!loading && !isStaleOpen && !isClockedIn && screenState === 'home' && (
          <>
            {weekSchedule && (
              <button
                type="button"
                className={styles.weekScheduleCard}
                data-testid="week-schedule-card"
                onClick={() => navigate('/schedule')}
              >
                <div className={styles.weekScheduleHeader}>
                  <span className={styles.cardTitle}>Lịch ca tuần này</span>
                  <span aria-hidden="true">›</span>
                </div>
                <div className={styles.weekScheduleGrid}>
                  {/* weekSchedule is built Monday-first (useAttendanceHomeExtras.ts
                      startOfWeekMonday), so the array index maps 1:1 onto
                      WEEKDAY_LABELS_VI — avoids re-parsing the YYYY-MM-DD string
                      through `new Date()` (UTC parse), which can shift the
                      apparent weekday by ±1 day depending on the viewer's
                      timezone offset. */}
                  {weekSchedule.map((day, index) => (
                    <div
                      key={day.date}
                      className={`${styles.weekScheduleDay} ${day.isToday ? styles.weekScheduleDayToday : ''}`}
                    >
                      <span className={styles.weekScheduleDayLabel}>{WEEKDAY_LABELS_VI[index]}</span>
                      {day.hasShift && <span className={styles.weekScheduleDayDot} aria-hidden="true">●</span>}
                    </div>
                  ))}
                </div>
                <span className={styles.weekScheduleLink}>Xem Lịch ►</span>
              </button>
            )}

            {urgentTasks.length > 0 && (
              <div className={styles.card} data-testid="urgent-tasks-card">
                <h2 className={styles.cardTitle}>Việc khẩn cấp ({urgentTasks.length})</h2>
                {urgentTasks.map((task) => (
                  <div key={task.id} className={styles.taskRow}>
                    <div className={styles.taskRowTop}>
                      <span>(!) {task.title}</span>
                      <span className={styles.taskPill}>Đề xuất</span>
                    </div>
                    {/* visual/style gap #3 — "Xem" → /tasks (Nhiệm vụ của
                        tôi), real target confirmed via WIREFRAME-EMPLOYEE-
                        FLOW.md:2840 + the mockup's own href; see this file's
                        header changelog for the full reconciliation.
                        2026-09-04 (structural parity) — added the deadline
                        meta line mobile's ListItem shows (`meta`), missing
                        here before; the "Xem" button stays (both coexist). */}
                    {task.deadline && (
                      <span className={styles.taskMeta}>
                        Hạn: {new Date(task.deadline).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    <div className={styles.taskRowActions}>
                      <button
                        type="button"
                        className={styles.taskViewButton}
                        onClick={() => navigate('/tasks')}
                        data-testid={`urgent-task-view-btn-${task.id}`}
                      >
                        Xem
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {adhocTasks.length > 0 && (
              <div className={styles.card} data-testid="adhoc-tasks-card">
                <h2 className={styles.cardTitle}>Việc phát sinh ({adhocTasks.length})</h2>
                {adhocTasks.map((task) => (
                  <div key={task.id} className={styles.taskRow}>
                    <div className={styles.taskRowTop}>
                      <span>{task.title}</span>
                      <span className={styles.taskPill}>Đề xuất</span>
                    </div>
                    {task.deadline && (
                      <span className={styles.taskMeta}>
                        Hạn: {new Date(task.deadline).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    <div className={styles.taskRowActions}>
                      <button
                        type="button"
                        className={styles.taskViewButton}
                        onClick={() => navigate('/tasks')}
                        data-testid={`adhoc-task-view-btn-${task.id}`}
                      >
                        Xem
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* web-sync checklist A.5 (2026-08-15) — branch/location fetch
                error + retry. 2026-09-04 (structural parity, root cause):
                scoped here (home/DEFAULT, just before the GPS status line)
                instead of page-global — mirrors mobile's Banner placement
                (AttendanceScreen.tsx:746-758). */}
            {branchConfigError && (
              <p role="alert" className={styles.branchConfigErrorBanner} data-testid="branch-config-error-banner">
                ⚠️ {branchConfigError}
                <button
                  type="button"
                  onClick={retryBranchConfig}
                  className={styles.retryButton}
                  data-testid="branch-config-retry-btn"
                >
                  Thử lại
                </button>
              </p>
            )}

            {branchConfig && (
              <p className={styles.gpsStatusLine} data-testid="gps-status-line">
                📍 {branchConfig.branch_name}
                {accuracyInfo && (
                  <>
                    {' '}
                    — Đã xác định · sai số ±{Math.round(accuracyInfo.accuracy)}m
                  </>
                )}
                {geofenceResult && (
                  <>
                    <br />📏 Cách {Math.round(geofenceResult.distance)}m
                  </>
                )}
              </p>
            )}
            <div className={styles.actionsRow}>
              <button
                onClick={() => setScreenState('method_select')}
                className={styles.primaryButton}
                disabled={loading}
                data-testid="clock-in-btn"
              >
                ● Chấm công vào
              </button>
            </div>
          </>
        )}

        {!loading && !isClockedIn && screenState === 'method_select' && (
          <MethodSelectScreen
            gpsMethodEnabled={methodConfig.selfie || methodConfig.gps}
            timemarkMethodEnabled={methodConfig.timemark}
            onCancel={() => setScreenState('home')}
            onContinue={(method) => {
              if (method === 'timemark') {
                setScreenState('home');
                triggerTimemark();
              } else {
                // [3] capture_photo trước — ảnh bắt buộc cho phương thức GPS,
                // cần có trước khi vào màn xác nhận (validateLocation không
                // phụ thuộc ảnh nên gọi song song, không cần chờ chụp xong).
                validateLocation();
                setScreenState('capture_photo');
              }
            }}
          />
        )}

        {/* SC-017-EMP-ATTENDANCE mobile-source-of-truth audit (2026-09-04):
            mobile's capture_photo has no in-component Cancel (required photo
            hides it — PhotoCapture.tsx:428 `!required &&`); back to
            method_select is via AppHeader's per-state back button
            (headerConfig.capture_photo.onBack → 'method_select',
            AttendanceScreen.tsx:1285). 2026-09-04 (structural parity, root
            causes #1/#2): this page's header is now state-driven
            (headerConfig.capture_photo above) and supplies that same
            back-target, so the duplicate in-body title+back header + `.card`
            wrapper (both added 2026-09-04 as a stopgap before the header
            became state-driven) are removed — CameraCapture now renders
            directly as a bare top-level sibling, matching mobile. */}
        {!loading && !isClockedIn && screenState === 'capture_photo' && (
          <CameraCapture
            required
            testIdPrefix="clockin-camera"
            onCapture={(dataUrl) => {
              capturePhoto(dataUrl);
              setScreenState('gps_clock_in_form');
            }}
          />
        )}

        {!loading && !isClockedIn && screenState === 'gps_clock_in_form' && (
          <ClockInConfirmScreen
            photoBase64={photoBase64}
            geofenceResult={geofenceResult}
            timingWarning={timingWarning}
            earlyClockinToleranceMinutes={earlyClockinToleranceMinutes}
            maxClockInDistanceMeters={branchConfig?.max_clock_in_distance_meters ?? null}
            loading={loading}
            errorMessage={error || message}
            accuracyInfo={accuracyInfo}
            canOverrideAccuracy={canOverrideAccuracy}
            onOverrideAccuracy={overrideAccuracy}
            onRetakePhoto={() => setScreenState('capture_photo')}
            // 2026-09-04 (structural parity, root cause #2): back-to-method_select
            // is now solely the page header's back button (headerConfig.gps_clock_in_form
            // above) — the redundant "‹ Đổi phương thức" button + this
            // onChangeMethod prop were removed from ClockInConfirmScreen.
            onUseTimemark={() => {
              setScreenState('home');
              triggerTimemark();
            }}
            onConfirm={async () => {
              const ok = await clockIn();
              if (ok) {
                setClockInSuccessTime(new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }));
                setScreenState('clock_in_success');
              }
            }}
          />
        )}

        {!loading && screenState === 'clock_in_success' && (
          <ClockInSuccessScreen
            time={clockInSuccessTime}
            branchName={branchConfig?.branch_name ?? null}
            gpsDeviationWarning={gpsDeviationWarning}
            // 2026-09-04 (chỉ đạo product owner): xong chấm công VÀO cũng ra
            // thẳng Trang chủ — trước đây bấm "Xong" chỉ quay về màn chấm
            // công, người dùng phải bấm thêm một lần nữa mới rời được.
            onDone={() => navigate('/dashboard/staff')}
            onViewChecklist={() => setScreenState('checklist')}
          />
        )}

      </main>

      {/* BOT-007 follow-up (2026-08-29, finding #1) — Timemark fallback modal.
          TimemarkForm is fully self-contained (own submit call, own
          success/error state) — only a trigger + overlay were missing here. */}
      {showTimemark && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-sm">
            {/* bot-060 (GH#871): nút "Về trang chủ" ở màn "Đã gửi — chờ Quản lý
                duyệt" trước đây dùng CHUNG callback với "Hủy"/X (closeTimemark)
                nên chỉ đóng modal — người dùng vẫn kẹt lại màn Chấm công, sai
                hẳn nhãn nút. onSuccess giờ đóng modal RỒI điều hướng thật về
                Trang chủ nhân viên, dùng lại đúng route các màn thành công khác
                trong file này (onDone/onGoHome → '/dashboard/staff').
                onCancel giữ nguyên "chỉ đóng": "Hủy"/X là hủy TRƯỚC khi gửi,
                người dùng chủ động ở lại màn Chấm công để chọn cách khác. */}
            <TimemarkForm
              // bot-060 follow-up (GH#871, 2026-09-16) — Employee path trước
              // đây KHÔNG có toast nào cả sau khi gửi Timemark thành công.
              // onSubmitted chạy NGAY tại thời điểm API success (không đợi
              // user bấm "Về trang chủ" như onSuccess bên dưới).
              onSubmitted={() => {
                setToast('Đã gửi yêu cầu Timemark — chờ Quản lý chi nhánh phê duyệt');
              }}
              onSuccess={() => {
                closeTimemark();
                navigate('/dashboard/staff');
              }}
              onCancel={closeTimemark}
            />
          </div>
        </div>
      )}

      {/* SC-017-EMP-ATTENDANCE [1] home STALE-OPEN (2026-09-04) — "[SHEET] xác
          nhận đóng ca tồn đọng" (wireframe lines 6188-6197). "Xác nhận" reuses
          the SAME real clockOut() mutation as every other clock-out on this
          page (mirrors mobile's handleStaleConfirm — no separate "close
          stale shift" endpoint exists on either platform). See this file's
          header for the known backend limitation: both clock-out endpoints
          only search for an open record from TODAY, so this can genuinely
          400 for a truly prior-day record — that error surfaces below via
          the hook's own `error` state rather than being swallowed. */}
      {showStaleConfirm && staleOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="stale-confirm-sheet">
          <div className={styles.card} style={{ maxWidth: 400, width: '100%' }}>
            <h2 className={styles.cardTitle}>Xác nhận chấm công ra cho ca hôm qua</h2>
            <p className={styles.timingWarningBanner}>
              ⚠ Ca này có thời lượng bất thường ({formatDurationVi(staleOpenMinutes ?? 0)}). Vui lòng liên hệ quản
              lý nếu cần điều chỉnh.
            </p>
            {error && (
              <p className={`${styles.statusMessage} ${styles.statusError}`} role="alert">
                {error}
              </p>
            )}
            {/* #780 (DD-0110 §2.2) — GPS blocked: tenant setting decides who closes. */}
            {staleCloseMode === 'employee_then_review' ? (
              <div data-testid="stale-close-no-gps">
                <p className={styles.statusMessage}>
                  Không lấy được vị trí? Đóng ca không cần vị trí — Quản lý duyệt sau, chưa tính công tới lúc đó.
                </p>
                <textarea
                  rows={2}
                  maxLength={500}
                  aria-label="Lý do đóng ca"
                  placeholder="Lý do (bắt buộc)"
                  value={staleReason}
                  onChange={(e) => setStaleReason(e.target.value)}
                  data-testid="stale-close-reason"
                />
                {staleCloseError && (
                  <p className={`${styles.statusMessage} ${styles.statusError}`} role="alert">
                    {staleCloseError}
                  </p>
                )}
                <button
                  type="button"
                  className={styles.secondaryButton}
                  disabled={staleClosing || !staleReason.trim()}
                  data-testid="stale-close-no-gps-btn"
                  onClick={async () => {
                    setStaleClosing(true);
                    setStaleCloseError(null);
                    const res = await apiWrapper.requestStaleClose(staleOpen.attendanceId, staleReason.trim());
                    setStaleClosing(false);
                    if (res.success) {
                      setShowStaleConfirm(false);
                      setStaleReason('');
                      setToast('Đã gửi yêu cầu đóng ca — chờ Quản lý duyệt');
                      recheckStaleOpen();
                    } else {
                      setStaleCloseError(res.error || 'Không gửi được yêu cầu. Vui lòng thử lại.');
                    }
                  }}
                >
                  Đóng ca không cần vị trí
                </button>
              </div>
            ) : (
              <p className={styles.statusMessage} data-testid="stale-ask-manager">
                Không lấy được vị trí? Nhờ Quản lý đóng ca.
              </p>
            )}
            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setShowStaleConfirm(false)}
                data-testid="stale-confirm-cancel"
              >
                Huỷ
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                disabled={loading}
                data-testid="stale-confirm-confirm"
                onClick={async () => {
                  const ok = await clockOut();
                  if (ok) {
                    setShowStaleConfirm(false);
                    recheckStaleOpen();
                  }
                }}
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {/* web-sync checklist A.2 (2026-08-15) — shared 5-tab bottom nav */}
      <EmployeeBottomNav />

      {/* bot-060 follow-up (GH#871) — toast, cùng pattern ManagerProfilePage.tsx */}
      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default AttendanceModern;
